import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

/**
 * Custom hook for connecting to sync logs WebSocket
 * Returns logs array and connection status
 */
const useSyncLogs = (isEnabled = false) => {
  const [logs, setLogs] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!isEnabled) {
      // If disabled, disconnect if connected
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setLogs([]);
      return;
    }

    // Create socket connection
    // Estrai l'URL base rimuovendo /api se presente
    const defaultApiBaseUrl =
      process.env.NODE_ENV === 'development'
        ? 'http://localhost:3002/api/'
        : 'https://crm.millergroup.it/api/';
    const apiBaseUrl = process.env.REACT_APP_BASE_URL || defaultApiBaseUrl;
    const basename = process.env.REACT_APP_BASENAME || '';
    // Socket.IO deve collegarsi all'origin. In demo il prefisso /demo viene
    // gestito esclusivamente da `path`; lasciarlo anche nell'URL lo trasforma
    // erroneamente nel namespace Socket.IO "/demo/".
    const socketUrl = new URL(apiBaseUrl, window.location.origin).origin;
    const socketPath = `${basename}/socket.io/`.replace('//', '/');

    const socket = io(socketUrl, {
      path: socketPath,
      withCredentials: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[WebSocket] Connected to server');
      setIsConnected(true);

      // Subscribe to sync logs
      socket.emit('subscribe:sync-logs');
    });

    socket.on('disconnect', () => {
      console.log('[WebSocket] Disconnected from server');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error);
      setIsConnected(false);
    });

    socket.on('sync-log', (logEntry) => {
      setLogs((prevLogs) => {
        // Evita duplicati basati su ID
        const exists = prevLogs.some(log => log.id === logEntry.id);
        if (exists) return prevLogs;

        // Aggiungi nuovo log
        const newLogs = [...prevLogs, logEntry];

        // Mantieni solo gli ultimi 500 log per evitare memory leak
        if (newLogs.length > 500) {
          return newLogs.slice(-500);
        }

        return newLogs;
      });
    });

    // Cleanup on unmount or when disabled
    return () => {
      if (socket) {
        socket.emit('unsubscribe:sync-logs');
        socket.disconnect();
      }
    };
  }, [isEnabled]);

  // Function to clear logs manually
  const clearLogs = () => {
    setLogs([]);
  };

  return {
    logs,
    isConnected,
    clearLogs
  };
};

export default useSyncLogs;
